import type { CSSProperties, InputHTMLAttributes, ReactNode } from "react";
import { cx } from "./cx";
import { VISUALLY_HIDDEN_INPUT } from "./fieldParts";

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  label?: ReactNode;
  hint?: string;
}

/** Single-choice control. Use RadioGroup for a labelled set. */
export function Radio({ label, hint, className, ...rest }: RadioProps) {
  return (
    <label className={cx("group relative flex cursor-pointer items-start gap-3 has-disabled:cursor-not-allowed", className)}>
      <input type="radio" className={VISUALLY_HIDDEN_INPUT} {...rest} />
      {/* The ring follows the native input's state, so controlled and uncontrolled use both work. */}
      <span
        className={cx(
          "mt-px flex size-5 flex-none items-center justify-center rounded-pill border bg-surface-card",
          "border-border-strong inset-shadow-default [transition:var(--transition-control)]",
          "peer-checked:border-green-700 peer-checked:inset-shadow-none peer-focus-visible:shadow-focus",
          "[&>span]:invisible peer-checked:[&>span]:visible",
        )}
      >
        <span className="size-2.5 rounded-pill bg-green-700" />
      </span>
      <span>
        <span className="block type-body [font-size:var(--text-base)] text-text-body group-has-disabled:text-ink-300">{label}</span>
        {hint && <span className="block type-small text-text-muted">{hint}</span>}
      </span>
    </label>
  );
}

export interface RadioGroupOption {
  value: string;
  label: string;
  hint?: string;
}

export interface RadioGroupProps {
  label?: string;
  name: string;
  value?: string;
  options?: Array<string | RadioGroupOption>;
  onChange?: (value: string) => void;
  className?: string;
  style?: CSSProperties;
}

/** Vertical group of radios sharing one name. */
export function RadioGroup({ label, name, value, options = [], onChange, className, style }: RadioGroupProps) {
  return (
    <fieldset className={cx("m-0 border-0 p-0", className)} style={style}>
      {label && <legend className="mb-3 p-0 type-label text-text-strong">{label}</legend>}
      <div className="flex flex-col gap-3">
        {options.map((option) => {
          const { value: optionValue, label: text, hint } =
            typeof option === "string" ? { value: option, label: option, hint: undefined } : option;
          return (
            <Radio
              key={optionValue}
              name={name}
              value={optionValue}
              label={text}
              hint={hint}
              checked={value === optionValue}
              onChange={() => onChange?.(optionValue)}
            />
          );
        })}
      </div>
    </fieldset>
  );
}
